import api from '@app/api'

import { CONTENT_TYPES } from '@app/api/constants'

import type { ResponseDto } from '@app/api/dtos'
import type { DefaultParams } from '@app/api/types'
import type {
  MonitoringValidationResultsConfigDto,
  MonitoringValidationConfigSpecialCasesDto,
  MonitoringValidationConfigStoresLinksDto,
  MonitoringValidationPriceRestorationConfigDto,
  MonitoringValidationPromoPriceTransitionConfigDto,
  MonitoringValidationSuperAbnormalConfigDto,
  MonitoringValidationConfigDto
} from './dtos'
import type { ExportExcel } from '../monitoringLists/types'
import type {
  DeleteMonitoringValidationConfigSpecialCasesData,
  DeleteMonitoringValidationConfigStoresLinksData,
  GetMonitoringValidationResultsConfigParams,
  PatchMonitoringValidationConfigData,
  PatchMonitoringValidationPriceRestorationConfigData,
  PatchMonitoringValidationPromoPriceTransitionConfigData,
  PatchMonitoringValidationResultsConfigData,
  PatchMonitoringValidationSuperAbnormalConfigData,
  PostMonitoringValidationConfigSpecialCasesData,
  PostMonitoringValidationConfigStoresLinksData
} from './types'

const URL = '/monitoring-validation/'

const DEFAULT_PARAMS = {
  limit: 1500
}

export const getMonitoringValidationResultsConfig = async (
  params: GetMonitoringValidationResultsConfigParams = DEFAULT_PARAMS
): Promise<ResponseDto<MonitoringValidationResultsConfigDto>> => {
  const url = `${URL}results/validations/config/`
  const response = await api.get<ResponseDto<MonitoringValidationResultsConfigDto>>(url, { params })

  return response.data
}

export const patchMonitoringValidationResultsConfig = async (
  data: PatchMonitoringValidationResultsConfigData
): Promise<void> => {
  const url = `${URL}results/validations/config/`

  await api.patch(url, data)
}

export const getMonitoringValidationConfig = async (): Promise<MonitoringValidationConfigDto> => {
  const url = `${URL}config/`
  const response = await api.get<MonitoringValidationConfigDto>(url)
  return response.data
}

export const getMonitoringValidationConfigSpecialCases = async (
  params: DefaultParams
): Promise<ResponseDto<MonitoringValidationConfigSpecialCasesDto>> => {
  const url = `${URL}config/special-cases/`
  const response = await api.get<ResponseDto<MonitoringValidationConfigSpecialCasesDto>>(url, { params })
  return response.data
}

export const postMonitoringValidationConfigSpecialCases = async (
  data: PostMonitoringValidationConfigSpecialCasesData
): Promise<void> => {
  const url = `${URL}config/special-cases/`
  await api.post(url, data)
}

export const deleteMonitoringValidationConfigSpecialCases = async ({
  id
}: DeleteMonitoringValidationConfigSpecialCasesData): Promise<void> => {
  const url = `${URL}config/special-cases/${id}/delete/`
  await api.delete(url)
}

export const patchMonitoringValidationConfig = async (data: PatchMonitoringValidationConfigData): Promise<void> => {
  const url = `${URL}config/`
  const response = await api.patch(url, data)
  return response.data
}

export const getMonitoringValidationPriceRestorationConfig =
  async (): Promise<MonitoringValidationPriceRestorationConfigDto> => {
    const url = `${URL}results/validations/price-restoration/config/`
    const response = await api.get<MonitoringValidationPriceRestorationConfigDto>(url)
    return response.data
  }

export const patchMonitoringValidationPriceRestorationConfig = async (
  data: PatchMonitoringValidationPriceRestorationConfigData
): Promise<void> => {
  const url = `${URL}results/validations/price-restoration/config/`
  const response = await api.patch(url, data)
  return response.data
}

export const getMonitoringValidationConfigStoresLinks = async (
  params: DefaultParams
): Promise<ResponseDto<MonitoringValidationConfigStoresLinksDto>> => {
  const url = `${URL}config/stores/links/`
  const response = await api.get<ResponseDto<MonitoringValidationConfigStoresLinksDto>>(url, { params })
  return response.data
}

export const postMonitoringValidationConfigStoresLinks = async (
  data: PostMonitoringValidationConfigStoresLinksData
): Promise<void> => {
  const url = `${URL}config/stores/links/`
  await api.post(url, data)
}

export const deleteMonitoringValidationConfigStoresLinks = async ({
  linkId
}: DeleteMonitoringValidationConfigStoresLinksData): Promise<void> => {
  const url = `${URL}config/stores/links/${linkId}/delete/`
  await api.delete(url)
}

export const exportMonitoringValidationStoresLinks = async (
  params: Omit<ExportExcel, 'id'>,
  signal?: AbortSignal
): Promise<Blob> => {
  const url = `${URL}config/stores/links/export/`

  // TODO: mock url, проверить/исправить как бэк будет готов
  const response = await api.get<Blob>(url, {
    signal,
    params,
    responseType: 'blob',
    headers: {
      'Content-Type': CONTENT_TYPES.applicationOfficedocumentSpreadsheetml
    }
  })
  return response.data
}

export const importMonitoringValidationStoresLinks = async (data: FormData): Promise<void> => {
  const url = `${URL}config/stores/links/import/`
  await api.post(url, data, {
    headers: {
      'Content-Type': CONTENT_TYPES.multipartFormData
    }
  })
}

export const getMonitoringValidationPromoPriceTransitionConfig =
  async (): Promise<MonitoringValidationPromoPriceTransitionConfigDto> => {
    const url = `${URL}results/validations/promo-price-transition/config/`
    const response = await api.get<MonitoringValidationPromoPriceTransitionConfigDto>(url)
    return response.data
  }

export const patchMonitoringValidationPromoPriceTransitionConfig = async (
  data: PatchMonitoringValidationPromoPriceTransitionConfigData
): Promise<void> => {
  const url = `${URL}results/validations/promo-price-transition/config/`
  const response = await api.patch(url, data)
  return response.data
}

export const getMonitoringValidationSuperAbnormalConfig =
  async (): Promise<MonitoringValidationSuperAbnormalConfigDto> => {
    const url = `${URL}results/validations/super-abnormal/config/`
    const response = await api.get<MonitoringValidationSuperAbnormalConfigDto>(url)
    return response.data
  }

export const patchMonitoringValidationSuperAbnormalConfig = async (
  data: PatchMonitoringValidationSuperAbnormalConfigData
): Promise<void> => {
  const url = `${URL}results/validations/super-abnormal/config/`
  const response = await api.patch(url, data)
  return response.data
}
